import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

// @mui material components
import {
  Menu,
  MenuItem,
  Badge,
  IconButton,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Button,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// API
import adminApi from "services/api";
import { useSocket } from "context/SocketContext";

function NotificationMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const socket = useSocket();

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds — kept as a fallback/resync
    // behind the real-time socket push below, not the primary channel.
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!socket) return undefined;
    const handleNewNotification = () => setUnreadCount((prev) => prev + 1);
    socket.on("notification:new", handleNewNotification);
    return () => socket.off("notification:new", handleNewNotification);
  }, [socket]);

  const fetchUnreadCount = async () => {
    try {
      const response = await adminApi.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getNotifications({ limit: 10 });
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await adminApi.markNotificationRead(id);
      // Update local state
      setNotifications((prev) =>
        prev.map((notif) => (notif._id === id ? { ...notif, read: true } : notif))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await adminApi.markAllNotificationsRead();
      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const getNotificationIcon = (notification) => {
    if (notification.type === "twilio_message") {
      return notification.metadata?.channel === "whatsapp" ? "💬" : "📱";
    }
    switch (notification.type) {
      case "consultation":
        return "📅";
      case "contact":
        return "✉️";
      case "bootcamp_application":
        return "🎓";
      default:
        return "🔔";
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate.toLocaleDateString();
  };

  return (
    <>
      <IconButton
        size="small"
        color="inherit"
        // Inherits the navbar's icon colour; it was hard-coded white and vanished on the light bar.
        sx={{ color: "inherit" }}
        onClick={handleOpen}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "calc(100vw - 24px)", sm: 380 },
            maxWidth: 380,
            maxHeight: 500,
            overflow: "auto",
            mt: 1,
          },
        }}
      >
        <MDBox px={2} py={1.5} display="flex" justifyContent="space-between" alignItems="center">
          <MDTypography variant="h6" fontWeight="medium">
            Notifications
          </MDTypography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllAsRead} sx={{ textTransform: "none" }}>
              Mark all read
            </Button>
          )}
        </MDBox>
        <Divider />

        {loading ? (
          <MDBox display="flex" justifyContent="center" p={3}>
            <CircularProgress size={30} />
          </MDBox>
        ) : notifications.length === 0 ? (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2" color="text">
              No notifications
            </MDTypography>
          </MDBox>
        ) : (
          notifications.map((notification, index) => (
            <div key={notification._id}>
              <MenuItem
                component={notification.link ? Link : "div"}
                to={notification.link || "#"}
                onClick={() => {
                  if (!notification.read) {
                    handleMarkAsRead(notification._id);
                  }
                  handleClose();
                }}
                sx={{
                  py: 1.5,
                  px: 2,
                  backgroundColor: notification.read ? "transparent" : "rgba(25, 118, 210, 0.08)",
                  "&:hover": {
                    backgroundColor: notification.read
                      ? "rgba(0, 0, 0, 0.04)"
                      : "rgba(25, 118, 210, 0.12)",
                  },
                }}
              >
                <MDBox display="flex" width="100%">
                  <Box mr={2} fontSize="1.5rem">
                    {getNotificationIcon(notification)}
                  </Box>
                  <MDBox flex={1}>
                    <MDTypography
                      variant="button"
                      fontWeight={notification.read ? "regular" : "bold"}
                      display="block"
                    >
                      {notification.title}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                      {notification.message}
                    </MDTypography>
                    <MDTypography variant="caption" color="text" fontSize="0.7rem">
                      {formatTime(notification.createdAt)}
                    </MDTypography>
                  </MDBox>
                  {notification.priority === "high" && (
                    <Box ml={1}>
                      <Typography fontSize="0.8rem" color="error">
                        ⚠️
                      </Typography>
                    </Box>
                  )}
                </MDBox>
              </MenuItem>
              {index < notifications.length - 1 && <Divider />}
            </div>
          ))
        )}
      </Menu>
    </>
  );
}

export default NotificationMenu;
